import { useEffect, useState, type Dispatch, type ReactNode, type SetStateAction } from "react";
import { AlertCircle, Save, X } from "lucide-react";
import type {
  CalidadDirectLikePayload,
  CalidadIndirectoPayload,
  CalidadSuministradorKind,
} from "../types/suministradores-homologados";
import { calidadShortMarkOptions } from "../types/suministradores-homologados";

type Props = {
  open: boolean;
  kind: CalidadSuministradorKind;
  mode: "create" | "edit";
  title: string;
  initialValues: CalidadIndirectoPayload | CalidadDirectLikePayload;
  submitting: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (
    values: CalidadIndirectoPayload | CalidadDirectLikePayload,
  ) => Promise<void>;
};

export function CalidadSuministradorFormModal({
  open,
  kind,
  mode,
  title,
  initialValues,
  submitting,
  error,
  onClose,
  onSubmit,
}: Props) {
  const [form, setForm] = useState(initialValues);

  useEffect(() => {
    if (!open) {
      return;
    }

    setForm(initialValues);
  }, [initialValues, open]);

  if (!open) {
    return null;
  }

  const isIndirecto = kind === "indirecto";
  const indirectoForm = form as CalidadIndirectoPayload;
  const setIndirectoForm = setForm as Dispatch<SetStateAction<CalidadIndirectoPayload>>;
  const directLikeForm = form as CalidadDirectLikePayload;
  const setDirectLikeForm = setForm as Dispatch<SetStateAction<CalidadDirectLikePayload>>;

  return (
    <div className="fixed inset-0 z-[110] grid place-items-center bg-slate-950/35 px-4 py-8 backdrop-blur-md">
      <div className="relative max-h-[92vh] w-full max-w-6xl overflow-hidden rounded-[36px] border border-[#dfe8e4] bg-white shadow-[0_44px_120px_rgba(15,23,42,.24)]">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-5 top-5 z-20 grid h-11 w-11 place-items-center rounded-2xl border border-slate-200 bg-white text-slate-500 shadow-[0_8px_20px_rgba(15,23,42,.05)]"
        >
          <X size={17} />
        </button>

        <div className="grid max-h-[92vh] overflow-y-auto lg:grid-cols-[0.88fr_1.12fr]">
          <div className="border-b border-[#dfe8e4] bg-[linear-gradient(135deg,#ffffff_0%,#f3fbf7_100%)] p-8 lg:border-b-0 lg:border-r">
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-emerald-50/85 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-emerald-700 shadow-[0_8px_22px_rgba(16,185,129,.08)]">
              {mode === "create" ? "Nuevo registro" : "Editar registro"}
            </div>

            <h2 className="mt-5 text-3xl font-black tracking-[-0.045em] text-slate-950 lg:text-4xl">
              {title}
            </h2>

            <p className="mt-4 max-w-md text-sm font-semibold leading-7 text-slate-500">
              {isIndirecto
                ? "Gestiona los campos de material, tipologia, autorizacion, uso web y nivel de confianza del suministrador."
                : "Gestiona la informacion operativa del suministrador homologado y su nivel de confianza."}
            </p>
          </div>

          <form
            className="space-y-5 bg-white p-8"
            onSubmit={async (event) => {
              event.preventDefault();
              await onSubmit(form);
            }}
          >
            <FieldBlock label="Nombre">
              <input
                value={form.nombre}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    nombre: event.target.value,
                  }))
                }
                className="field-control"
                required
              />
            </FieldBlock>

            {isIndirecto ? (
              <div className="grid gap-5">
                <ShortMarkFieldset
                  title="Material / Servicio"
                  fields={[
                    ["material_servicio_m", "M"],
                    ["material_servicio_t", "T"],
                    ["material_servicio_g", "G"],
                    ["material_servicio_a", "A"],
                  ]}
                  values={indirectoForm}
                  onChange={setIndirectoForm}
                />

                <ShortMarkFieldset
                  title="Tipologia"
                  fields={[
                    ["tipologia_dir", "DIR"],
                    ["tipologia_ind", "IND"],
                    ["tipologia_pun", "PUN"],
                    ["tipologia_con", "CON"],
                    ["tipologia_nac", "NAC"],
                    ["tipologia_loc", "LOC"],
                  ]}
                  values={indirectoForm}
                  onChange={setIndirectoForm}
                />

                <div className="grid gap-4 md:grid-cols-3">
                  <ShortMarkSelect
                    label="Autorizacion"
                    value={indirectoForm.autorizacion ?? ""}
                    onChange={(value) =>
                      setForm((current) => ({
                        ...current,
                        autorizacion: value,
                      }))
                    }
                  />
                  <ShortMarkSelect
                    label="Uso web PLAN"
                    value={indirectoForm.uso_web_plan ?? ""}
                    onChange={(value) =>
                      setForm((current) => ({
                        ...current,
                        uso_web_plan: value,
                      }))
                    }
                  />
                  <ShortMarkSelect
                    label="Uso web DOC"
                    value={indirectoForm.uso_web_doc ?? ""}
                    onChange={(value) =>
                      setForm((current) => ({
                        ...current,
                        uso_web_doc: value,
                      }))
                    }
                  />
                </div>

                <ShortMarkFieldset
                  title="Nivel de confianza"
                  fields={[
                    ["nivel_confianza_ca", "CA"],
                    ["nivel_confianza_cm", "CM"],
                    ["nivel_confianza_cb", "CB"],
                    ["nivel_confianza_pc", "PC"],
                    ["nivel_confianza_nc", "NC"],
                  ]}
                  values={indirectoForm}
                  onChange={setIndirectoForm}
                />

                <FieldBlock label="Observaciones / interes">
                  <textarea
                    value={indirectoForm.observaciones_interes ?? ""}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        observaciones_interes: event.target.value,
                      }))
                    }
                    className="field-control min-h-[150px]"
                  />
                </FieldBlock>
              </div>
            ) : (
              <div className="grid gap-5">
                <FieldBlock label="Servicio">
                  <textarea
                    value={directLikeForm.servicio ?? ""}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        servicio: event.target.value,
                      }))
                    }
                    className="field-control min-h-[120px]"
                  />
                </FieldBlock>

                <FieldBlock label="Comentarios">
                  <textarea
                    value={directLikeForm.comentarios ?? ""}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        comentarios: event.target.value,
                      }))
                    }
                    className="field-control min-h-[120px]"
                  />
                </FieldBlock>

                <ShortMarkFieldset
                  title="Nivel de confianza"
                  fields={[
                    ["nivel_confianza_ca", "CA"],
                    ["nivel_confianza_cm", "CM"],
                    ["nivel_confianza_cb", "CB"],
                    ["nivel_confianza_pc", "PC"],
                    ["nivel_confianza_nc", "NC"],
                  ]}
                  values={directLikeForm}
                  onChange={setDirectLikeForm}
                />

                <FieldBlock label="Observaciones / interes">
                  <textarea
                    value={directLikeForm.observaciones_interes ?? ""}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        observaciones_interes: event.target.value,
                      }))
                    }
                    className="field-control min-h-[150px]"
                  />
                </FieldBlock>
              </div>
            )}

            {error && (
              <div className="rounded-[28px] border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">
                <div className="flex items-start gap-2">
                  <AlertCircle size={16} className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              </div>
            )}

            <div className="flex flex-wrap justify-end gap-3 border-t border-[#edf1f3] pt-5">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-black text-slate-600"
              >
                Cancelar
              </button>

              <button
                type="submit"
                disabled={submitting || !form.nombre.trim()}
                className="inline-flex items-center gap-2 rounded-full bg-[linear-gradient(135deg,#059669,#10b981,#14b8a6)] px-6 py-3 text-sm font-black text-white shadow-[0_18px_40px_rgba(16,185,129,.24)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Save size={16} />
                {submitting
                  ? "Guardando..."
                  : mode === "create"
                    ? "Crear registro"
                    : "Guardar cambios"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function FieldBlock({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-2 block text-xs font-black uppercase tracking-[0.16em] text-slate-400">
        {label}
      </span>
      {children}
    </label>
  );
}

function ShortMarkFieldset<T extends Record<string, string | null | undefined>>({
  title,
  fields,
  values,
  onChange,
}: {
  title: string;
  fields: Array<[keyof T, string]>;
  values: T;
  onChange: Dispatch<SetStateAction<T>>;
}) {
  return (
    <div className="calidad-suppliers-fieldset">
      <div className="calidad-suppliers-fieldset-title">{title}</div>
      <div className="grid gap-4 md:grid-cols-3 xl:grid-cols-5">
        {fields.map(([field, label]) => (
          <ShortMarkSelect
            key={String(field)}
            label={label}
            value={values[field] ?? ""}
            onChange={(value) =>
              onChange((current) => ({
                ...current,
                [field]: value,
              }))
            }
          />
        ))}
      </div>
    </div>
  );
}

function ShortMarkSelect({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="block">
      <span className="mb-2 block text-xs font-black uppercase tracking-[0.16em] text-slate-400">
        {label}
      </span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="field-control"
      >
        {calidadShortMarkOptions.map((option) => (
          <option key={option || "empty"} value={option}>
            {option === "" ? "Vacio" : option}
          </option>
        ))}
      </select>
    </label>
  );
}
